import { supabase } from '@/lib/supabase';
import { projectId } from '@/utils/supabase/info';

const BASE = `https://${projectId}.supabase.co/functions/v1/api`;

async function req<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  if (!session?.access_token) throw new Error('No active session. Please sign in.');

  const res = await fetch(`${BASE}${endpoint}`, {
    ...options,
    headers: {
      Authorization: `Bearer ${session.access_token}`,
      'Content-Type': 'application/json',
      ...(options.headers || {}),
    },
  });

  if (!res.ok) {
    const e = await res.json().catch(() => ({}));
    // Blockers are the useful part of a 409 — surface them rather than a status code.
    if (e.blockers?.length) throw new Error(e.blockers.join(' • '));
    throw new Error(e.error || `HTTP ${res.status}: ${res.statusText}`);
  }
  return res.json() as Promise<T>;
}

export type Bucket = 'team' | 'performance' | 'owner';

export interface PayoutLine {
  memberId: string;
  name: string;
  bucket: Bucket;
  amountCents: number;
  note: string;
}

export interface PayoutTotals {
  serviceRevenueCents: number;
  overheadCents: number;
  teamCompCents: number;
  performanceRevenueCents: number;
  eligibleSharesCents: number;
  ownerPoolCents: number;
  ownerEachCents: number;
  actualMarginPct: number | null;
}

export interface QueuedRun {
  clientId: string;
  clientName: string;
  period: string;
  ok: boolean;
  blockers: string[];
  narrative: string | null;
  totals: PayoutTotals | null;
  lines: PayoutLine[];
  warnings: string[];
  receivedCents: number;
  expectedCents: number;
  funded: boolean;
  existingRun: { id: string; status: string; approved_at: string | null } | null;
  /**
   * How the receipts funding this run were tied to the period. 'applied' means
   * they were matched to this period's obligations. 'settled-month' means they
   * merely settled inside the calendar month, which misattributes any late
   * payment — so it comes with a caveat the approver must read.
   */
  attribution: 'applied' | 'settled-month';
  attributionCaveat: string | null;
  /**
   * What the approval actually recorded. Null until a run is approved. For an
   * approved run this — not the live recomputation — is what the card shows.
   */
  recorded: {
    totals: { ownerEachCents?: number } | null;
    lines: Array<{ memberId: string | null; name: string; bucket: string; amountCents: number }>;
    drift: { recordedOwnerEachCents: number; liveOwnerEachCents: number } | null;
  } | null;
}

export interface PayoutQueue {
  period: string;
  enabledClientCount: number;
  runs: QueuedRun[];
}

export interface ApproveResult {
  runId: string;
  status: string;
  distributions: number;
  unrouted?: number;
  narrative: string | null;
  note: string;
}

export const payoutsAPI = {
  queue: (period?: string) =>
    req<PayoutQueue>(`/payouts/queue${period ? `?period=${period}` : ''}`),

  run: (clientId: string, period: string) =>
    req<{ run: QueuedRun }>(`/payouts/run/${clientId}/${period}`),

  /** The confirmation string is required by the server, not just the UI. */
  approve: (clientId: string, period: string) =>
    req<ApproveResult>(`/payouts/run/${clientId}/${period}/approve`, {
      method: 'POST',
      body: JSON.stringify({ confirm: 'APPROVE' }),
    }),
};

export function formatCents(cents: number): string {
  return `$${(cents / 100).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

// ── Receipt resolution queue ─────────────────────────────────────────────────

export interface QueuedReceipt {
  id: string;
  amountCents: number;
  currency: string;
  kind: 'service' | 'performance' | 'unclassified';
  source: string;
  counterparty: string | null;
  status: string;
  settledAt: string | null;
  entrySource: 'ingested' | 'manual';
  /**
   * The matcher's opinion, never applied. There is no virtual-account table, so
   * nothing can auto-confirm — this exists to tell a human WHY it needs them.
   */
  suggestion: {
    status: 'confirmed' | 'flagged' | 'unmatched';
    reason: string;
    clientId: string | null;
    clientName: string | null;
    explanation: string;
    candidates: string[];
  };
}

export interface PeriodNeed {
  period: string;
  stream: string;
  owedCents: number;
  fundedCents: number;
  remainingCents: number;
}

export const receiptsAPI = {
  queue: () => req<{ receipts: QueuedReceipt[]; count: number }>('/receipts/queue'),

  periodsFor: (receiptId: string, clientId: string) =>
    req<{ periods: PeriodNeed[] }>(`/receipts/${receiptId}/periods?clientId=${clientId}`),

  resolve: (
    receiptId: string,
    clientId: string,
    kind: 'service' | 'performance',
    legs: Array<{ period: string; amountCents: number }>,
  ) =>
    req<{ ok: boolean; result: unknown }>(`/receipts/${receiptId}/resolve`, {
      method: 'POST',
      body: JSON.stringify({ clientId, kind, legs }),
    }),

  unapply: (receiptId: string) =>
    req<{ ok: boolean }>(`/receipts/${receiptId}/unapply`, { method: 'POST' }),

  manual: (amountCents: number, counterparty: string, settledAt: string, source = 'manual') =>
    req<{ ok: boolean; id: string }>('/receipts/manual', {
      method: 'POST',
      body: JSON.stringify({ amountCents, counterparty, settledAt, source }),
    }),
};
